import { ReactNode, useState } from 'react';
import TextField from '@mui/material/TextField';
import { Autocomplete as MuiAutocomplete } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from 'assets/iconComponents/SearchIcon';
import { palette } from 'view/themes/palette';
import * as S from './styled';

type OptionShape = { id?: string | number; [key: string]: any };

type DropdownAutocompleteProps<T extends OptionShape> = {
  label?: string;
  placeholder?: string;
  value: T | null;
  onChange: (value: T | null) => void;
  getOptionLabel: (option: T) => string;
  inputValue?: string;
  setInputValue?: (value: string) => void;
  data?: T[] | null;
  isLoading?: boolean;
  marginBottom?: number | string;
  /** @deprecated use `marginBottom` */
  marginbottom?: number | string;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  getOptionDisabled?: (option: T) => boolean;
  renderOptionEnd?: (option: T, isSelected: boolean) => ReactNode;
};

const toCssMargin = (m: number | string | undefined): string => {
  if (m === undefined || m === null) {
    return '16px';
  }
  return typeof m === 'number' ? `${m}px` : m;
};

const defaultIsOptionEqualToValue = <T extends OptionShape>(option: T, value: T): boolean => {
  if (option?.id != null && value?.id != null) {
    return option.id === value.id;
  }
  return option === value;
};

const DropdownAutocomplete = <T extends OptionShape>({
  label = 'Select...',
  placeholder = 'Select...',
  value,
  onChange,
  getOptionLabel,
  inputValue,
  setInputValue,
  data,
  isLoading,
  marginBottom,
  marginbottom,
  isOptionEqualToValue = defaultIsOptionEqualToValue,
  getOptionDisabled,
  renderOptionEnd,
}: DropdownAutocompleteProps<T>) => {
  const [open, setOpen] = useState(false);

  return (
    <S.Wrapper style={{ marginBottom: toCssMargin(marginBottom ?? marginbottom) }}>
      {label && <S.SelectLabel>{label}</S.SelectLabel>}
      <MuiAutocomplete<T, false, true, false>
        disabled={isLoading || !data}
        open={open}
        onOpen={() => {
          return setOpen(true);
        }}
        onClose={() => {
          return setOpen(false);
        }}
        value={value ?? undefined}
        onChange={(_, newValue) => {
          return onChange(newValue);
        }}
        inputValue={inputValue}
        onInputChange={(_, next) => {
          setInputValue?.(next);
        }}
        getOptionLabel={getOptionLabel}
        getOptionKey={(option) => {
          return option?.id ?? getOptionLabel(option);
        }}
        isOptionEqualToValue={isOptionEqualToValue}
        getOptionDisabled={getOptionDisabled}
        disableClearable
        options={data ?? []}
        renderOption={(props, option, { selected }) => {
          const { key, ...rest } = props as typeof props & { key: string };
          return (
            <S.AutocompleteOption {...rest} key={key} isSelected={selected}>
              <S.OptionLabel>{getOptionLabel(option)}</S.OptionLabel>
              <S.OptionEnd>
                {renderOptionEnd?.(option, selected)}
                {selected && (
                  <S.Checkmark>
                    <CheckIcon />
                  </S.Checkmark>
                )}
              </S.OptionEnd>
            </S.AutocompleteOption>
          );
        }}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              placeholder={placeholder}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <SearchIcon />
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
              sx={{
                '.MuiInputBase-root': {
                  minHeight: 40,
                  color: palette.text.body,
                  borderRadius: '8px',
                  padding: '8px 12px',

                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: palette.border.default,
                    borderRadius: '8px',
                  },

                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: palette.hover.brand,
                  },

                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px',
                    borderColor: palette.primary.purple500,
                  },

                  '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                    borderColor: palette.alerts.error,
                  },
                },
              }}
            />
          );
        }}
        sx={{ width: '100%' }}
        popupIcon={<KeyboardArrowDownIcon />}
        slotProps={{
          popupIndicator: {
            sx: {
              color: palette.unknown.icon_grey,
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            },
          },
          popper: { sx: { borderRadius: '8px' } },
          paper: {
            sx: {
              marginTop: '2px',
              borderRadius: '8px',
              boxShadow:
                '0px 20px 24px -4px rgba(0, 0, 0, 0.08), 0px 8px 8px -4px rgba(0, 0, 0, 0.03)',
              '.MuiAutocomplete-noOptions': { padding: '8px 12px' },
            },
          },
          listbox: {
            sx: {
              border: 'none',
              borderRadius: '8px',
              maxHeight: 245,
              overflowY: 'auto',
              padding: 0,
              '&::-webkit-scrollbar': { width: '4px' },
              '&::-webkit-scrollbar-thumb': {
                background: '#D8DBE8',
                borderRadius: '10px',
                transition: 'background 0.2s ease-in-out',
              },
              '&::-webkit-scrollbar-thumb:hover': { background: '#BFC3D4' },
              '&::-webkit-scrollbar-thumb:active': { background: '#A5AABF' },
            },
          },
        }}
      />
    </S.Wrapper>
  );
};

export default DropdownAutocomplete;
